import { type ComponentProps, useCallback, useRef, useState } from 'react'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/helpers/utils'

const TOOLTIP_DELAY_MS = 1000

interface TruncatedTextProps extends ComponentProps<'span'> {
	children: string
}

// Single-line text that ellipses when overflowing its container and reveals
// the full string in a tooltip after a 1s hover delay. The tooltip is only
// mounted when overflow is detected so non-truncated text behaves normally.
export const TruncatedText = ({
	children,
	className,
	...props
}: TruncatedTextProps) => {
	const observerRef = useRef<ResizeObserver | null>(null)
	const [isOverflowing, setIsOverflowing] = useState(false)

	// Callback ref instead of a one-shot effect: toggling the tooltip wrapper
	// below swaps the span node, so we must re-attach the ResizeObserver to the
	// current node every time — otherwise it orphans onto the old node and the
	// overflow measurement freezes (tooltip never appears / never clears).
	const measureRef = useCallback((el: HTMLSpanElement | null) => {
		observerRef.current?.disconnect()
		if (!el) {
			return
		}
		const update = () => {
			setIsOverflowing(el.scrollWidth > el.clientWidth)
		}
		update()
		const observer = new ResizeObserver(update)
		observer.observe(el)
		observerRef.current = observer
	}, [])

	const text = (
		<span
			className={cn('block truncate', className)}
			ref={measureRef}
			{...props}
		>
			{children}
		</span>
	)

	if (!isOverflowing) {
		return text
	}

	return (
		<Tooltip
			content={children}
			delayDuration={TOOLTIP_DELAY_MS}
			isOpenOnClickEnabled={false}
			triggerProps={{ asChild: true }}
		>
			{text}
		</Tooltip>
	)
}
